
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigResponse} from './ConfigResponse';
import {FieldModelConfigResponse} from './ConfigResponse';

/**
 * ConfigValidateResponse struct
 */
class ConfigValidateResponse extends ConfigResponse {
  /**
   * Initialize struct
   * @param {!ConfigResponse=} parent
   * @param {!boolean=} isValid
   * @param {!Array=} errors
   * @constructor
   */
  constructor (parent = new ConfigResponse(), isValid = false, errors = []) {
    super()
    super.copy(parent)
    this.IsValid = isValid
    this.Errors = errors
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ConfigValidateResponse}
   * @param {!ConfigValidateResponse} other Other struct
   * @returns {!ConfigValidateResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.IsValid != null) {
      this.IsValid = other.IsValid
    } else {
      this.IsValid = null
    }
    if (other.Errors != null) {
      this.Errors = []
      for (let item of other.Errors) {
        if (item != null) {
          let tempItem
          tempItem = item
          this.Errors.push(tempItem)
        } else {
          this.Errors.push(null)
        }
      }
    } else {
      this.Errors = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ConfigValidateResponse}
   * @returns {!ConfigValidateResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ConfigValidateResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ConfigValidateResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ConfigValidateResponse}
   * @param {!ConfigValidateResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ConfigValidateResponse)) {
      throw new TypeError('Instance of ConfigValidateResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ConfigValidateResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      IsValid: ((this.IsValid != null) ? this.IsValid : null),
      Errors: ((this.Errors != null) ? Array.from(this.Errors, item => ((item != null) ? item : null)) : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ConfigValidateResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ConfigValidateResponse} other Object value
   * @returns {!ConfigValidateResponse} Created struct
   */
  static fromObject (other) {
    return new ConfigValidateResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ConfigValidateResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ConfigValidateResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ConfigValidateResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6028
  }
}

export { ConfigValidateResponse };

/**
 * Fast Binary Encoding ConfigValidateResponse field model
 */
class FieldModelConfigValidateResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigResponse(buffer, 4 + 4)
    this.IsValid = new fbe.FieldModelBool(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.Errors = new fbe.FieldModelVector(new fbe.FieldModelString(buffer, this.IsValid.fbeOffset + this.IsValid.fbeSize), buffer, this.IsValid.fbeOffset + this.IsValid.fbeSize)
  }

  /**
   * Get the ConfigResponse field model
   * @this {!FieldModelConfigValidateResponse}
   * @returns {!FieldModelConfigResponse} ConfigResponse field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelConfigValidateResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelConfigValidateResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.IsValid.fbeSize + this.Errors.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelConfigValidateResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.IsValid.fbeExtra + this.Errors.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelConfigValidateResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelConfigValidateResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelConfigValidateResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6028
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelConfigValidateResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelConfigValidateResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.IsValid.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.IsValid.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsValid.fbeSize

    if ((fbeCurrentSize + this.Errors.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Errors.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Errors.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelConfigValidateResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelConfigValidateResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelConfigValidateResponse}
   * @param {!ConfigValidateResponse} fbeValue Default value, defaults is new ConfigValidateResponse()
   * @returns {!ConfigValidateResponse} ConfigValidateResponse value
   */
  get (fbeValue = new ConfigValidateResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelConfigValidateResponse}
   * @param {!ConfigValidateResponse} fbeValue ConfigValidateResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.IsValid.fbeSize) <= fbeStructSize) {
      fbeValue.IsValid = this.IsValid.get()
    } else {
      fbeValue.IsValid = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsValid.fbeSize

    if ((fbeCurrentSize + this.Errors.fbeSize) <= fbeStructSize) {
      this.Errors.get(fbeValue.Errors)
    } else {
      fbeValue.Errors.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Errors.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelConfigValidateResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelConfigValidateResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelConfigValidateResponse}
   * @param {!ConfigValidateResponse} fbeValue ConfigValidateResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelConfigValidateResponse}
   * @param {ConfigValidateResponse} fbeValue ConfigValidateResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.IsValid.set(fbeValue.IsValid)
    this.Errors.set(fbeValue.Errors)
  }
}

export { FieldModelConfigValidateResponse };

/**
 * Fast Binary Encoding ConfigValidateResponse model
 */
class ConfigValidateResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelConfigValidateResponse(this.buffer, 4)
  }

  /**
   * Get the ConfigValidateResponse model
   * @this {!ConfigValidateResponseModel}
   * @returns {!FieldModelConfigValidateResponse} model ConfigValidateResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ConfigValidateResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ConfigValidateResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ConfigValidateResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ConfigValidateResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelConfigValidateResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ConfigValidateResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ConfigValidateResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ConfigValidateResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ConfigValidateResponseModel}
   * @param {!ConfigValidateResponse} value ConfigValidateResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ConfigValidateResponseModel}
   * @param {!ConfigValidateResponse} value ConfigValidateResponse value, defaults is new ConfigValidateResponse()
   * @return {!object} Deserialized ConfigValidateResponse value and its size
   */
  deserialize (value = new ConfigValidateResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ConfigValidateResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ConfigValidateResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ConfigValidateResponseModel}
   * @param {!number} prev Previous ConfigValidateResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ConfigValidateResponseModel };
